import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';

import type { ClusterListResponseOutputItemsItem } from '@/api/generated';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ClusterCardProps {
  cluster: ClusterListResponseOutputItemsItem;
}

export const ClusterCard: FC<ClusterCardProps> = ({
  cluster,
}: ClusterCardProps) => {
  const { t } = useTranslation();
  const complete = cluster.waiting === 0;

  return (
    <Link
      data-testid='cluster-card'
      to='/app/clusters/$clusterId'
      params={{ clusterId: cluster.id }}
      search={(prev) => prev}>
      <Card
        className={cn(
          'group h-full gap-0 transition-shadow duration-200 hover:shadow-md',
          cluster.lockedBy && 'opacity-60'
        )}>
        <CardContent className='bg-muted relative aspect-[4/3] overflow-hidden'>
          {cluster.imagePath && (
            <img
              className='size-full object-contain transition-transform duration-200 group-hover:scale-[var(--image-scale)]'
              alt=''
              src={cluster.imagePath}
              loading='lazy'
              decoding='async'
            />
          )}
          {cluster.lockedBy && (
            <Badge
              data-testid='cluster-card-locked'
              variant='secondary'
              className='absolute top-2 left-2'>
              <Lock />
              {cluster.lockedBy}
            </Badge>
          )}
          <Badge
            variant={complete ? 'success' : 'default'}
            className='absolute top-2 right-2'>
            {complete
              ? t('clusters.complete')
              : t('clusters.waiting', { count: cluster.waiting })}
          </Badge>
        </CardContent>
        <CardDescription className='flex flex-col gap-1 px-3 pt-2 pb-3'>
          <span className='text-foreground line-clamp-2 text-sm font-semibold'>
            {cluster.name}
          </span>
          <span className='text-xs'>
            <span className='font-mono'>#{cluster.id}</span> ·{' '}
            {t('clusters.fronts', { count: cluster.fronts })}
          </span>
          <span className='text-xs'>
            {[
              cluster.category,
              cluster.markets
                .map((market) => market.replace(/^RETAIL-/u, ''))
                .join(' '),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </CardDescription>
      </Card>
    </Link>
  );
};
